import React, { useState, useEffect } from "react"; // useEffect is not used, can be removed if no other use emerges
import { Link, useNavigate } from "react-router-dom";
import { Auth } from "aws-amplify";
import { useUser } from "../contexts/UserContext";
import { useTranslation } from 'react-i18next'; // ADDED
import { useMessagingPhase } from '../contexts/MessagingPhaseContext'; // ADDED
import log from "../utils/logger";
import { GA, GaStyle, getGaTheme, toggleGaTheme } from "./glonetTheme";

const NavBar = ({ isAuthenticated, setIsAuthenticated }) => {
  const { currentPhase } = useMessagingPhase(); // ADDED
  const { t } = useTranslation(currentPhase); // ADDED

  const [isOpen, setIsOpen] = useState(false);
  const [gaTheme, setGaTheme] = useState(getGaTheme);
  const handleThemeToggle = () => setGaTheme(toggleGaTheme());
  const themeGlyph = gaTheme === "dark" ? "☀" : "☾"; // shows the theme you'd switch to
  const themeLabel = gaTheme === "dark" ? "Light mode" : "Dark mode";
  const { user } = useUser();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await Auth.signOut();
      setIsAuthenticated(false);
      navigate("/");
    } catch (error) {
      log.error("Error signing out: ", error);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    // MODIFIED FOR I18N
    if (hour < 12) return t('navBar.greetingGoodMorning', "Good morning");
    if (hour < 18) return t('navBar.greetingGoodAfternoon', "Good afternoon");
    return t('navBar.greetingGoodEvening', "Good evening");
  };

  return (
    <nav style={{ background: "var(--ga-nav-bg)", borderBottom: "1px solid var(--ga-panel-border)", backdropFilter: "blur(8px)" }}>
      <GaStyle />
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center flex-shrink-0">
            <Link to="/" style={{ fontFamily: GA.serif, fontSize: 22, color: GA.text, textDecoration: "none", letterSpacing: "-0.01em" }}>
              {/* MODIFIED FOR I18N */}
              {t('navBar.appName', 'Glonet')}
            </Link>
            {isAuthenticated && user && (
              <span className="text-sm font-medium ml-4 hidden md:inline" style={{ color: GA.muted }}>
                {getGreeting()}, {user.given_name || user.email}
              </span>
            )}
          </div>

          <div className="hidden md:flex items-center justify-center flex-1">
            {isAuthenticated && (
              <div className="flex space-x-4 absolute left-1/2 transform -translate-x-1/2">
                <Link
                  to="/dashboard"
                  className="ga-link px-3 py-2 rounded-md text-sm font-medium"
                >
                  {/* MODIFIED FOR I18N */}
                  {t('navBar.dashboardLink', 'Dashboard')}
                </Link>
                <Link
                  // This link points to `/events` which lists user's created/attended items.
                  // In phase1, this should be "My Nexuses"
                  to="/events" 
                  className="ga-link px-3 py-2 rounded-md text-sm font-medium"
                >
                  {/* MODIFIED FOR I18N */}
                  {t('navBar.userItemsLink', 'My Items')}
                </Link>
                <Link
                  // This link points to `/glonet-events` which is the public listing.
                  // In phase1, this should be "Explore Nexuses" or simply "Nexuses"
                  to="/nexuses" 
                  className="ga-link px-3 py-2 rounded-md text-sm font-medium"
                >
                  {/* MODIFIED FOR I18N */}
                  {t('navBar.mainLink', 'Explore')}
                </Link>
                <Link
                  to="/profile"
                  className="ga-link px-3 py-2 rounded-md text-sm font-medium"
                >
                  {/* MODIFIED FOR I18N */}
                  {t('navBar.profileLink', 'Profile')}
                </Link>
                {user && user.is_admin && (
                  <Link
                    to="/admin"
                    data-testid="admin-link"
                    className="px-3 py-2 rounded-md text-sm font-medium"
                    style={{ color: "#eab308" }}
                  >
                    {t('navBar.adminLink', 'Admin')}
                  </Link>
                )}
              </div>
            )}
          </div>

          <div className="hidden md:flex items-center flex-shrink-0">
            <button
              onClick={handleThemeToggle}
              aria-label={themeLabel}
              title={themeLabel}
              className="ga-btn mr-3 inline-flex items-center justify-center"
              style={{ width: 32, height: 32, borderRadius: 999, background: "transparent",
                       border: "1px solid var(--ga-ghost-border)", color: GA.text,
                       fontFamily: GA.mono, fontSize: 14, lineHeight: 1, cursor: "pointer" }}
            >
              {themeGlyph}
            </button>
            {isAuthenticated ? (
              <button
                onClick={handleLogout}
                className="ga-btn ga-btn-primary px-4 py-2 rounded-full text-sm font-semibold" style={{ color: GA.onAccent, background: GA.accent, border: "none" }}
              >
                {/* MODIFIED FOR I18N */}
                {t('navBar.logOutButton', 'Log Out')}
              </button>
            ) : (
              <Link
                to="/signin"
                className="ga-link px-3 py-2 rounded-md text-sm font-medium"
              >
                {/* MODIFIED FOR I18N */}
                {t('navBar.logInButton', 'Log In')}
              </Link>
            )}
          </div>

          <div className="md:hidden flex items-center justify-end flex-1">
            {isAuthenticated && user && (
              <span className="text-sm font-medium absolute left-1/2 transform -translate-x-1/2" style={{ color: GA.muted }}>
                {getGreeting()}, {user.given_name || user.email}
              </span>
            )}
            {isAuthenticated && user && user.is_admin && (
              <Link
                to="/admin"
                data-testid="admin-link-mobile"
                className="px-2 py-2 text-sm font-medium"
                style={{ color: "#eab308" }}
              >
                {t('navBar.adminLink', 'Admin')}
              </Link>
            )}
            <button
              className="inline-flex items-center justify-center p-2 rounded-md focus:outline-none" style={{ color: GA.body }}
              onClick={() => setIsOpen(!isOpen)}
            >
              {/* sr-only text is for accessibility, can be translated if needed, but often kept simple */}
              <span className="sr-only">Open main menu</span>
              {isOpen ? (
                <svg
                  className="block h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              ) : (
                <svg
                  className="block h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div className={`${isOpen ? "block" : "hidden"} md:hidden`}>
        <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
          <button
            onClick={handleThemeToggle}
            className="ga-link block w-full text-left px-3 py-2 rounded-md text-base font-medium"
            style={{ background: "transparent", border: "none", cursor: "pointer" }}
          >
            <span style={{ fontFamily: GA.mono, marginRight: 8 }}>{themeGlyph}</span>
            {themeLabel}
          </button>
          {isAuthenticated ? (
            <>
              <Link
                to="/dashboard"
                className="ga-link block px-3 py-2 rounded-md text-base font-medium"
                onClick={() => setIsOpen(false)} // Close menu on click
              >
                {t('navBar.dashboardLink', 'Dashboard')}
              </Link>
              <Link
                to="/events"
                className="ga-link block px-3 py-2 rounded-md text-base font-medium"
                onClick={() => setIsOpen(false)}
              >
                {t('navBar.userItemsLink', 'My Items')}
              </Link>
              <Link
                to="/nexuses"
                className="ga-link block px-3 py-2 rounded-md text-base font-medium"
                onClick={() => setIsOpen(false)}
              >
                {t('navBar.mainLink', 'Explore')}
              </Link>
              <Link
                to="/profile"
                className="ga-link block px-3 py-2 rounded-md text-base font-medium"
                onClick={() => setIsOpen(false)}
              >
                {t('navBar.profileLink', 'Profile')}
              </Link>
              <button
                onClick={() => { handleLogout(); setIsOpen(false); }}
                className="ga-link block w-full text-left px-3 py-2 rounded-md text-base font-medium" style={{ background: "transparent", border: "none", cursor: "pointer" }}
              >
                {t('navBar.logOutButton', 'Log Out')}
              </button>
            </>
          ) : (
            <Link
              to="/signin"
              className="ga-link block px-3 py-2 rounded-md text-base font-medium"
              onClick={() => setIsOpen(false)}
            >
              {t('navBar.logInButton', 'Log In')}
            </Link>
          )}
        </div>
      </div>
    </nav>
  );
};

export default NavBar;